import React, { useEffect } from 'react';
import { DatePicker, Form, Input, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import isNil from 'lodash/isNil';
import moment from 'moment';

import BookingActionBar from '../../../components/NewBooking/BookingActionBar';
import {
  BookingForm,
  FieldRow,
  FormCardBody,
} from '../../../components/NewBooking/BookingForm';
import { Card, SectionTitle } from '../../../components/common/Card';
import { updateBookingData } from '../../../slices/booking';
import { STATES } from '../../../constants/states';
import { TIME_FORMAT } from '../../../constants/time';
import { normaliseStateValue } from '../../../utils/addressUtils';
import { toPhoneDigits } from '../../../utils/phoneUtils';
import { scrollToFirstFormError } from '../../../utils/scrollUtils';
import analytics from '../../../infra/analytics';

const { Option } = Select;

const DATE_OF_BIRTH_HINT = 'Must match your Medicare card.';

const PersonalDetails = () => {
  const [form] = Form.useForm();
  const { workflowId } = useParams();
  const bookingData = useSelector(({ booking }) => booking.data);
  const workflowData = useSelector(({ workflow }) => workflow.data);
  const dispatch = useDispatch();

  useEffect(() => {
    analytics.logPageViewEvent('fill_personal_details', {
      workflow_id: workflowId,
    });
  }, [workflowId]);

  useEffect(() => {
    if (!isNil(bookingData.personalDetails)) {
      form.setFieldsValue(bookingData.personalDetails);
      return;
    }

    if (isNil(workflowData.client)) return;

    const {
      firstName,
      lastName,
      preferredName,
      dateOfBirth,
      email,
      phoneNumberMobile,
      addressResidential,
    } = workflowData.client;
    const { state, postcode, streetAddress, suburb } = addressResidential || {};

    form.setFieldsValue({
      firstName,
      lastName,
      preferredName,
      dateOfBirth: dateOfBirth
        ? moment(dateOfBirth, TIME_FORMAT.DATE_WITH_DASH)
        : undefined,
      mobile: phoneNumberMobile,
      email,
      address: streetAddress,
      suburb,
      state: normaliseStateValue(state),
      postcode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingData.personalDetails, form]);

  const onFinish = async () => {
    try {
      const values = await form.validateFields();
      dispatch(updateBookingData({ personalDetails: values }));

      return true;
    } catch (errorInfo) {
      scrollToFirstFormError(form, errorInfo);
      console.log('Person details failed:', errorInfo);
      return false;
    }
  };

  return (
    <>
      <BookingForm layout="vertical" form={form}>
        <Card>
          <FormCardBody>
            <SectionTitle>Patient</SectionTitle>

            <FieldRow>
              <Form.Item
                label="PATIENT First Name (As per Medicare card)"
                required
                name="firstName"
                rules={[
                  { required: true, message: 'Please input your first name.' },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="lastName"
                label="PATIENT Surname (As per Medicare card)"
                required
                rules={[
                  { required: true, message: 'Please input your last name.' },
                ]}
              >
                <Input />
              </Form.Item>
            </FieldRow>

            <Form.Item name="preferredName" label="Preferred Name">
              <Input />
            </Form.Item>

            <Form.Item
              name="dateOfBirth"
              label="Date Of Birth"
              required
              extra={DATE_OF_BIRTH_HINT}
              rules={[
                {
                  type: 'object',
                  required: true,
                  message: 'Please enter your date of birth.',
                },
              ]}
            >
              <DatePicker
                placeholder="DD/MM/YYYY"
                format={TIME_FORMAT.TABLE_TIME}
              />
            </Form.Item>

            <FieldRow>
              <Form.Item
                name="mobile"
                label="Mobile"
                required
                rules={[
                  {
                    required: true,
                    message: 'Please input your phone number.',
                  },
                  {
                    transform: toPhoneDigits,
                    pattern: /^(61|0)[0-9]{9}$/,
                    message: 'Please input a valid phone number.',
                  },
                ]}
              >
                <Input placeholder="04** *** ***" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                required
                rules={[
                  { required: true, message: 'Please input your email.' },
                  {
                    type: 'email',
                    message: 'Please input a valid email address.',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </FieldRow>
          </FormCardBody>
        </Card>

        <Card>
          <FormCardBody>
            <SectionTitle>Address</SectionTitle>

            <Form.Item label="Address" name="address">
              <Input />
            </Form.Item>

            <Form.Item label="Suburb or Town" name="suburb">
              <Input placeholder="Please input suburb or town" />
            </Form.Item>

            <FieldRow>
              <Form.Item label="State" name="state">
                <Select placeholder="Please select state">
                  {STATES.map(({ value, label }) => (
                    <Option value={value} key={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Post Code"
                name="postcode"
                required
                rules={[
                  { required: true, message: 'Please input your postcode.' },
                  {
                    pattern: /^(\d{4})$/,
                    message: 'Please input a valid postcode.',
                  },
                ]}
              >
                <Input placeholder="Please input postcode" />
              </Form.Item>
            </FieldRow>
          </FormCardBody>
        </Card>
      </BookingForm>

      <BookingActionBar onNext={onFinish} />
    </>
  );
};

export default PersonalDetails;
